/**
 * NotesLinkRow — card row that opens the full-screen notes editor.
 * Shared by the user's own profile and family-member edit screens.
 */

import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { Colors, Shadows } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';

interface NotesLinkRowProps {
  notesCount: number;
  onPress: () => void;
}

export function NotesLinkRow({ notesCount, onPress }: NotesLinkRowProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const subtitle =
    notesCount === 0
      ? 'Add notes for AI context'
      : `${notesCount} note${notesCount === 1 ? '' : 's'} — used for AI context`;

  return (
    <TouchableOpacity
      style={[styles.card, Shadows.sm, { backgroundColor: palette.cardBackground }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={styles.row}>
        <View style={styles.left}>
          <Ionicons name="document-text-outline" size={20} color={palette.primary} />
          <View style={styles.info}>
            <Text style={[styles.title, { color: palette.text, fontFamily: getBodyFont() }]}>
              Notes
            </Text>
            <Text style={[styles.subtitle, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
              {subtitle}
            </Text>
          </View>
        </View>
        <Ionicons name="chevron-forward" size={20} color={palette.textMuted} />
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: BorderRadius.md,
    padding: Spacing.lg,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  left: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    flex: 1,
  },
  info: {
    flex: 1,
  },
  title: {
    fontSize: 16,
    fontWeight: '500',
  },
  subtitle: {
    fontSize: 12,
    marginTop: Spacing.xxs,
  },
});
